import { FeatureFlagProvider } from '@/contexts/FeatureFlagsContext'
import { AccountContextProvider } from '@/contexts/account-context'
import { StockAlertsProvider } from '@/contexts/alerts-context'
import { FavoriteProvider } from '@/contexts/favorites-context'
import { StoreProvider } from '@/contexts/store-context'
import queryClient from '@/core/tanstack-query'
import { lightTheme } from '@/styles/theme'
import { Loader, MantineProvider, type MantineThemeOverride, type Tuple } from '@mantine/core'
import { QueryClientProvider } from '@tanstack/react-query'
import { Provider as JotaiProvider } from 'jotai'
import { ReactNode, Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import defaultSeo from '../../next-seo.config'
import MaintenanceProvider from '@/contexts/maintenance'
import { SeoHead } from '@/shared/seo/SeoHead'

export type Branding = {
    primaryColor: string
    primaryColorLight: string
    primaryColorDark: string
    btnColor: string
    btnColorHover: string
}

function buildTheme(branding: Branding): MantineThemeOverride {
    const shades: Tuple<string, 10> = [
        branding.primaryColorLight,
        branding.primaryColorLight,
        branding.primaryColorLight,
        branding.primaryColor,
        branding.primaryColor,
        branding.primaryColor,
        branding.primaryColor,
        branding.primaryColorDark,
        branding.primaryColorDark,
        branding.primaryColorDark,
    ]
    return {
        ...lightTheme,
        colors: { ...lightTheme.colors, brand: shades },
        primaryColor: 'brand',
    }
}

export default function Providers({ children, branding }: { children: ReactNode; branding?: Branding }) {
    const theme = branding ? buildTheme(branding) : lightTheme
    return (
        <QueryClientProvider client={queryClient}>
            <SeoHead {...defaultSeo} />
            {branding && (
                <style>{`:root { --primary: ${branding.primaryColor}; --primary-light: ${branding.primaryColorLight}; --primary-dark: ${branding.primaryColorDark}; --btn: ${branding.btnColor}; --btn-hover: ${branding.btnColorHover}; }`}</style>
            )}
            <MantineProvider
                withGlobalStyles
                withNormalizeCSS
                theme={theme}
            >
                <ErrorBoundary
                    FallbackComponent={({ error }) => {
                        console.log({ error })
                        return <p>Error : {error.message}</p>
                    }}
                >
                    <Suspense
                        fallback={
                            <div className="flex flex-col items-center justify-center h-full gap-y-4">
                                <Loader color="brand" />
                                <span className="mt-2 text-sm text-center text-neutral-500">
                                    Chargement en cours...
                                </span>
                            </div>
                        }
                    >
                        <JotaiProvider>
                            <MaintenanceProvider>
                                <FeatureFlagProvider>
                                    <StoreProvider>
                                        <AccountContextProvider>
                                            <FavoriteProvider>
                                                <StockAlertsProvider>
                                                    {children}
                                                </StockAlertsProvider>
                                            </FavoriteProvider>
                                        </AccountContextProvider>
                                    </StoreProvider>
                                </FeatureFlagProvider>
                            </MaintenanceProvider>
                        </JotaiProvider>
                    </Suspense>
                </ErrorBoundary>
            </MantineProvider>
        </QueryClientProvider>
    )
}
